<script lang="ts">
    
    import { LngLatBounds } from "mapbox-gl";
    import { onMount, setContext } from "svelte";
    import { navigate } from "svelte-navigator";
    import { fade } from 'svelte/transition';
    import CheckpointMarker from "../Components/MapView/CheckpointMarker.svelte";
    import CheckpointModal from "../Components/MapView/CheckpointModal.svelte";
    import Compass from "../Components/MapView/Compass.svelte";
    import MapView from "../Components/MapView/MapView.svelte";
    import MilestoneMarker from "../Components/MapView/MilestoneMarker.svelte";
    import MilestoneModal from "../Components/MapView/MilestoneModal.svelte";
    import PlayerMarker from "../Components/MapView/PlayerMarker.svelte";
    import Activity from "../Components/Shared/Activity.svelte";
    import Button from "../Components/Shared/Button.svelte";
    import ErrorScreen from "../Components/Shared/ErrorScreen.svelte";
    import GameClock from "../Components/Shared/GameClock.svelte";
    import GamePoints from "../Components/Shared/GamePoints.svelte";
    import Geolocating from "../Components/Shared/Geolocating.svelte";
    import IconFollow from "../Components/Shared/IconFollow.svelte";
    import TargetsModal from "../Components/Shared/TargetsModal.svelte";
    import VideoModal from "../Components/Shared/VideoModal.svelte";
    import View from "../Components/Shared/View.svelte";
    import WinScreen from "../Components/Shared/WinScreen.svelte";
    import type { AccessibilityStatementDto, AnyActivity, CoordinatesDto, Fn, InventoryItemDto, MilestoneDto } from "../global";
    import type { Checkpoint } from "../Model/Checkpoint";
    import type { CheckpointState } from "../Model/CheckpointState";
    import type { Game } from "../Model/Game";
    import { calcBounds, awaitTime } from "../Services";
    import { disableFollowModeTemporarily, followMode, isFollowModeEnabled, toggleFollowMode } from "../Services/follow";
    import { createGameState, loadGame, saveGame, setCheckpointAsTarget, setEndGameDate, setStartGameDate } from "../Services/game";
    import { calcDistance } from "../Services/geolocation";
    import { getMilestones } from "../Services/milestones";

    const checkpointMarkers: CheckpointMarker[] = [];
    
    let game: Game;
    let gameStateCopy: CheckpointState[];
    let mapContainer: HTMLElement;
    let mapView: MapView;
    let showMapShadow = false;
    let isGameEnd = false;
    let currentCheckpoint: Checkpoint;
    let currentActivity: AnyActivity;
    let openTooFar = false;
    let openNeedGeolocation = false;
    let milestones: MilestoneDto[];
    let openMilestoneModal = false;
    let currentMilestoneIndex: number;
    let maintainMilestoneModalOpen = false;
    let videoSrc: string;
    let openTargetsModal = false;
    let mapBackBounds: LngLatBounds;
    let isWinScreenOpen = false;
    let activityPoints: number;
    let onContinueResolve: Fn;

    setContext('getGame', () => game);
    setContext('saveGame', () => saveGame(game));
    setContext('toggleTooFar', toggleTooFar);
    setContext('toggleNeedGeolocation', toggleNeedGeolocation);
    setContext('openActivity', openActivity);
    setContext('openWinScreen', openWinScreen);

    onMount(async () => {
        const currentGame = loadGame();
        
        if (!currentGame) {
            navigate('/');
            return;
        }

        if (currentGame.allCheckpointsCompleted()) {
            navigate('/fin-game');
            return;
        }

        game = currentGame;
        gameStateCopy = createGameState(game);
        milestones = await getMilestones();
        playStartVideo();
        setStartGameDate();
    });

    function openWinScreen(points: number): Promise<void> {
        return new Promise(resolve => {
            onContinueResolve = resolve;
            activityPoints = points;
            isWinScreenOpen = true;
        });
    }

    function toggleTooFar(): void {
        openTooFar = !openTooFar;
    }

    function toggleNeedGeolocation(): void {
        openNeedGeolocation = !openNeedGeolocation; 
    }

    async function setCompleted(checkpoint: Checkpoint): Promise<void> {

        if (currentCheckpoint || checkpoint.isCompleted.value) {
            return;
        }

        // await showEarnedInventoryItemsWhenCompleted(checkpoint);

        //Buscamos la copia de estado del checkpoint y la establecemos como completada
        //Luego usaremos los cambios de estado detectados para saber que checkpoints
        //se verán afectados por el cambio de estado real
        const checkpointState = gameStateCopy.find(({ id }) => id === checkpoint.id);
        checkpointState.isCompleted.set(true);

        //Comprobamos que checkpoints tienen un estado de
        //visibilidad o accesibilidad distinto a la de su copia de estado
        const affectedCheckpoints = game.checkpoints.filter(checkpoint => {
            const checkpointState = gameStateCopy.find(({ id }) => id === checkpoint.id);
            return (
                   checkpoint.isVisible.value !== checkpointState.isVisible.value
                || checkpoint.isAccesible.value !== checkpointState.isAccesible.value
            );
        });

        //Si no hay ningún checkpoint afectado simplemente
        //establecemos el estado y retornamos.
        if (affectedCheckpoints.length === 0) {
            await setCompletedAndCheckGameEnded(checkpoint);
            return;
        }

        showMapShadow = true;

        //Obtenemos los bounds actuales y los relativos
        //a los checkpoints afectados
        const prevBounds = mapBackBounds || mapView.getBounds();
        const changeBounds = new LngLatBounds(calcBounds(affectedCheckpoints));
        mapBackBounds = undefined;

        //Calculamos la duración de la animación del mapa
        //en función a la distancia a la que se encuentren
        //los centros de los bounds de mapa calculados
        const prevCenter = prevBounds.getCenter();
        const changeCenter = prevBounds.getCenter();
        const distance = calcDistance(
            { lon: prevCenter.lng, lat: prevCenter.lat },
            { lon: changeCenter.lng, lat: changeCenter.lat },
        );

        //Calculamos la duración de la animación
        //100 milisegundos por cada 100 metros
        const mapAnimationDuration = minmax(500, 1000, (distance / 100) * 100);

        //Movemos el mapa de manera que enfoque todos
        //los checkpoints afectados
        await mapView.fitBounds(changeBounds, {
            padding: 50,
            maxZoom: mapView.getZoom() + 2,
            duration: mapAnimationDuration
        });

        //Establecemos el chckpoint como completado
        await setCompletedAndCheckGameEnded(checkpoint);

        //Devolvemos el mapa a los bounds previos
        await mapView.fitBounds(prevBounds, { duration: mapAnimationDuration });

        showMapShadow = false;
    }

    async function showEarnedInventoryItem(item: InventoryItemDto): Promise<void> {
        return new Promise(resolve => {
            resolve();
        });
    }

    function playStartVideo(): void {
        if (!game.startVideo || localStorage.getItem('start_video_played')) {
            return;
        }
        localStorage.setItem('start_video_played', '1')
        videoSrc = game.startVideo;
    }

    async function playEndVideo(): Promise<void> {
        return new Promise(resolve => {
            if (!game.endVideo || localStorage.getItem('end_video_played')) {
                resolve();
                return;
            }
            localStorage.setItem('end_video_played', '1')
            videoSrc = game.endVideo;

            resolve();
        });

    }

    async function setCompletedAndCheckGameEnded(checkpoint: Checkpoint): Promise<void> {
        checkpoint.setCompleted();
        saveGame(game);

        const firstTarget = game.getFirstTarget();
        if (firstTarget) {
            setCheckpointAsTarget(firstTarget);
        }
        
        //Esperamos 1 segundo por animaciones que puedan tener lugar
        await awaitTime(1000);

        if (!game.allCheckpointsCompleted()) {
            return;
        }

        isGameEnd = true;

        await playEndVideo();
        
    }

    function minmax(min: number, max: number, num: number): number {
        return Math.max(Math.min(num, max), min);
    }

    function createSideEffectChecker(checkpoint: Checkpoint): (accStat: AccessibilityStatementDto) => boolean {
        return ({ targets, isCompleted }: AccessibilityStatementDto): boolean => {
            return isCompleted !== undefined && targets.includes(checkpoint.id);
        };
    }

    async function onCheckpointClick({ detail: checkpoint }: CustomEvent<Checkpoint>): Promise<void> {
        if (currentCheckpoint?.id === checkpoint.id) {
            return;
        }

        disableFollowModeTemporarily(true);

        if (!isFollowModeEnabled()) {
            //Guardamos los bounds actuales del mapa
            //para volver a ellos en cuanto el modal
            //del checkpoint sea cerrado
            mapBackBounds = mapView.getBounds();
        }

        await fitMapByPosition(checkpoint.position, checkpoint.marker.height);
        currentCheckpoint = checkpoint;
    }

    async function fitMapByPosition(position: CoordinatesDto, offsetY = 0): Promise<void> {
        const { height } = mapContainer.getBoundingClientRect();
        const modalBottom = Math.min(height - 50, height * 0.7);

        await mapView.panTo(position, {
            duration: 400,
            offset: [0, (modalBottom - (height / 2)) + offsetY]
        });
    }

    function onMapMovestart(): void {
        closeCheckpointModal();
        if (!maintainMilestoneModalOpen) {
            onMilestoneClose();
        }
    }

    async function closeCheckpointModal(): Promise<void> {
        if (currentActivity || !currentCheckpoint) {
            return;
        }
        const checkpoint = currentCheckpoint;
        currentCheckpoint = undefined;
        if (checkpoint.allActivitiesAreCompleted()) {
            //Esperamos un momento a que termine
            //la animación de cierre del modal de checkpoint
            //antes de marcar el checkpoint como completado
            await awaitTime(500);
            await setCompleted(checkpoint);
        }

        if (mapBackBounds) {
            await mapView.fitBounds(mapBackBounds);
            mapBackBounds = undefined;
        }

        disableFollowModeTemporarily(false);
    }

    function onCompassClick(): void {
        toggleTargetsModal();
    }

    function toggleTargetsModal(): void {
        openTargetsModal = !openTargetsModal;
    }

    async function onMilestoneClick({ detail: milestone }: CustomEvent<MilestoneDto>): Promise<void> {
        disableFollowModeTemporarily(true);

        if (!isFollowModeEnabled()) {
            //Guardamos los bounds actuales del mapa
            //para volver a ellos en cuanto el modal
            //de milestone sea cerrado
            mapBackBounds = mapView.getBounds();
        }

        maintainMilestoneModalOpen = true;
        currentMilestoneIndex = milestones.findIndex(({ id }) => id === milestone.id);
        await fitMapByPosition(milestone.position, 16);
        maintainMilestoneModalOpen = false;
        openMilestoneModal = true;
    }

    function onMilestoneClose(): void {
        if (!openMilestoneModal) {
            return;
        }
        openMilestoneModal = false;
        disableFollowModeTemporarily(false);

        if (mapBackBounds) {
            mapView.fitBounds(mapBackBounds);
            mapBackBounds = undefined;
        }
    }

    function openActivity(activity: AnyActivity): void {
        currentActivity = activity;
    }

    function onActivityClose(): void {
        currentActivity = undefined;
    }

    async function onMilestoneSliderChange({ detail: milestone }: CustomEvent<MilestoneDto>): Promise<void> {
        maintainMilestoneModalOpen = true;
        await fitMapByPosition(milestone.position, 16);
        maintainMilestoneModalOpen = false;
    }

    function onCloseVideoModal(): void {
        videoSrc = undefined;

        if (isGameEnd) {
            setEndGameDate();
            navigate('/fin-game')
        }
    }

    function onWinContinue(): void {
        isWinScreenOpen = false;
        onContinueResolve?.();
        activityPoints = undefined;
        onContinueResolve = undefined;
    }

</script>

{#if game}

    {#if videoSrc}
        <VideoModal src={videoSrc} on:close={onCloseVideoModal} />
    {/if}

    {#if isWinScreenOpen}
        <WinScreen points={activityPoints} on:continue={onWinContinue} />
    {/if}

    <Geolocating />

    <View flex>
        <div class="header">
            <GameClock />
            <GamePoints />
        </div>
        <div class="map" bind:this={mapContainer}>

            <div class="compass-container">
                <Compass on:click={onCompassClick} />
            </div>

            {#if openTargetsModal}
                <TargetsModal {game} on:close={toggleTargetsModal} />
            {/if}

            {#if showMapShadow}
                <div class="map-shadow" transition:fade></div>
            {/if}

            <MapView bind:this={mapView} on:movestart={onMapMovestart}>

                <PlayerMarker />

                {#each game.checkpoints as checkpoint, index}
                    <CheckpointMarker {checkpoint} bind:this={checkpointMarkers[index]} on:click={onCheckpointClick} />
                {/each}

                {#if milestones}
                    {#each milestones as milestone}
                        <MilestoneMarker {milestone} on:click={onMilestoneClick} />
                    {/each}
                {/if}

            </MapView>

            {#if currentCheckpoint || openMilestoneModal}
                <div class="modal-container">
                    {#if currentCheckpoint}
                        <CheckpointModal
                            checkpoint={currentCheckpoint}
                            on:close={closeCheckpointModal} />
                    {:else if openMilestoneModal}
                        <MilestoneModal
                            {milestones}
                            milestoneIndex={currentMilestoneIndex}
                            on:close={onMilestoneClose}
                            on:change={onMilestoneSliderChange} />
                    {/if}
                </div>
            {/if}

            <button type="button" class="toggle-follow-btn" class:enabled={$followMode} on:click={toggleFollowMode}>
                <IconFollow />
            </button>

        </div>
    </View>

    {#if currentActivity}
        <Activity activity={currentActivity} on:close={onActivityClose} />
    {/if}

    {#if openTooFar}
        <ErrorScreen>
            <img src="/images/too-far.svg" alt="">
            <h4>Estas muy lejos</h4>
            <p class="text-center">Acércate para poder interactuar.</p>
            <Button color="secondary" on:click={toggleTooFar}>Continuar</Button>
        </ErrorScreen>
    {/if}

    {#if openNeedGeolocation}
        <ErrorScreen>
            <img src="/images/no-geolocated.svg" alt="">
            <h4>Sin geolocalizar</h4>
            <p class="text-center">Aún no hemos podido geolocalizarte.</p>
            <Button color="secondary" on:click={toggleNeedGeolocation}>Continuar</Button>
        </ErrorScreen>
    {/if}

{/if}

<style lang="scss">

    .header {
        height: 60px;
        background-color: var(--color-primary);
        color: #fff;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-left: 15px;
        padding-right: 15px;
    }

    .map {
        flex: 1 1 0;
        position: relative;
    }

    .compass-container {
        $size: 75px;
        position: absolute;
        z-index: 9999;
        width: $size;
        height: $size;
        bottom: 10px;
        right: calc(50% - #{$size * 0.5});
    }

    .map-shadow {
        position: absolute;
        z-index: 5;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        box-shadow: inset 0 0 50px rgba(0, 0, 0, 0.25);
    }

    .modal-container {
        position: absolute;
        z-index: 9999;
        top: 0;
        right: 0;
        left: 0;
        pointer-events: none;
        touch-action: none;
        height: calc(100% - 50px);
        max-height: 70%;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
    }

    .toggle-follow-btn {
        position: absolute;
        z-index: 20000;
        left: 10px;
        bottom: 10px;
        background-color: var(--color-primary);
        border: none;
        border-radius: 4px;
        opacity: 0.5;
        padding: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 37px;
        height: 37px;

        &.enabled {
            opacity: 1;
        }
    }

</style>
